import React from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
  useParams,
} from "react-router-dom";
import Layout from "./components/layout";
import SuspenseWrapper from "./components/wrapper/suspenseWrapper";
import { WithAdminAccess } from "./components/wrapper/admin";
import {
  AddDomainLazy,
  AddNewProjectLazy,
  AllFoldersLazy,
  AllPagesLazy,
  CompleteStepLazy,
  CreateFormLazy,
  CreateTemplateLazy,
  DomainSetupLazy,
  EditCategoryLazy,
  EditFormLazy,
  EditProjectLazy,
  EditTemplateCategoryLazy,
  FolderPageLazy,
  GetQRCodeLazy,
  NewCategoryLazy,
  NewFolderLazy,
  NewTemplateCategoryLazy,
  PageBlockerLazy,
  PageCategoryLazy,
  PageSettingsLazy,
  PaymentLazy,
  ProfileLazy,
  ProjectPageLazy,
  ProjectsLazy,
  ShadedPagesLazy,
  ShareSettingsLazy,
  ShowCategoryLazy,
  ShowTemplateCategoryLazy,
  ShowTemplateLazy,
  StatisticsLazy,
  SubmissionsLazy,
  SubpageTemplatesLazy,
  SuccessfulPaymentLazy,
  SupportLazy,
  TemplateCategoryLazy,
  TemplatesLazy,
  UpdateTemplateLazy,
  UserAdministrationLazy,
} from "./pages";

const LocaleLayout = () => {
  const { locale } = useParams();
  const location = useLocation();
  if (locale === "en") {
    const newPath = location.pathname.slice(`/${locale}`.length) || "/";
    return <Navigate to={newPath + location.search + location.hash} replace />;
  }
  return <Layout />;
};

const routes = (locale: string | undefined = "") => [
  {
    path: `/${locale}`,
    element: <LocaleLayout />,
    children: [
      {
        element: <SuspenseWrapper />,
        children: [
          { path: "", element: <Navigate to="projects" replace /> },
          {
            path: "payment",
            element: <PaymentLazy />,
          },
          {
            path: "payment/successful",
            element: <SuccessfulPaymentLazy />,
          },
          {
            path: "projects",
            element: <ProjectsLazy />,
          },
          {
            path: "projects/new",
            element: <AddNewProjectLazy />,
          },
          {
            path: "projects/new/:projectId/setup",
            element: <DomainSetupLazy />,
          },
          {
            path: "projects/new/:projectId/complete",
            element: <CompleteStepLazy />,
          },
          {
            path: "projects/:id/add-domain",
            element: <AddDomainLazy />,
          },
          {
            path: "projects/:id/add-subdomain",
            element: <AddDomainLazy />,
          },
          {
            path: "projects/:projectId/edit",
            element: <EditProjectLazy />,
          },
          {
            path: "projects/:projectId",
            element: <ProjectPageLazy />,
          },
          {
            path: "profile",
            element: <ProfileLazy />,
          },
          {
            path: "page/:id/edit",
            element: <PageSettingsLazy />,
          },
          {
            path: "page/create",
            element: <PageSettingsLazy />,
          },
          {
            path: "projects/:projectId/folder/:id",
            element: <FolderPageLazy />,
          },
          {
            path: "folder/new",
            element: <NewFolderLazy />,
          },
          {
            path: "user/access/:item/:id/access",
            element: <ShareSettingsLazy />,
          },
          {
            path: "page/category",
            element: (
              <WithAdminAccess>
                <PageCategoryLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "page/category/new",
            element: (
              <WithAdminAccess>
                <NewCategoryLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "page/category/:categoryId/edit",
            element: (
              <WithAdminAccess>
                <EditCategoryLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "page/category/:categoryId",
            element: (
              <WithAdminAccess>
                <ShowCategoryLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "template",
            element: (
              <WithAdminAccess>
                <TemplatesLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "template/new",
            element: (
              <WithAdminAccess>
                <CreateTemplateLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "template/:templateId/edit",
            element: (
              <WithAdminAccess>
                <UpdateTemplateLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "template/:templateId",
            element: (
              <WithAdminAccess>
                <ShowTemplateLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "template/category",
            element: (
              <WithAdminAccess>
                <TemplateCategoryLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "template/category/new",
            element: (
              <WithAdminAccess>
                <NewTemplateCategoryLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "template/category/:categoryId",
            element: (
              <WithAdminAccess>
                <ShowTemplateCategoryLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "template/category/:categoryId/edit",
            element: (
              <WithAdminAccess>
                <EditTemplateCategoryLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "shared",
            element: <ShadedPagesLazy />,
          },
          {
            path: "support",
            element: <SupportLazy />,
          },
          {
            path: "entry/:pageId",
            element: <GetQRCodeLazy />,
          },
          {
            path: "statistic-dashboard",
            element: <StatisticsLazy />,
          },
          {
            path: "statistic-dashboard/:pageId",
            element: <StatisticsLazy />,
          },
          {
            path: "page/all",
            element: (
              <WithAdminAccess>
                <AllPagesLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "folder/all",
            element: (
              <WithAdminAccess>
                <AllFoldersLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "user-administration",
            element: (
              <WithAdminAccess>
                <UserAdministrationLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "page-blocker",
            element: (
              <WithAdminAccess>
                <PageBlockerLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "subpage-templates",
            element: (
              <WithAdminAccess>
                <SubpageTemplatesLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "subpage-templates/create",
            element: (
              <WithAdminAccess>
                <CreateFormLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "subpage-templates/:templateId/edit",
            element: (
              <WithAdminAccess>
                <EditFormLazy />
              </WithAdminAccess>
            ),
          },
          {
            path: "submissions",
            element: <SubmissionsLazy />,
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter([...routes(), ...routes(":locale")]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
